import React, { useMemo, useState } from 'react';

import { Skeleton, Stack } from '@mui/material';
import dayjs from 'dayjs';

import LeaderboardRow from '../../../../components/LeaderboardRow';
import SkeletonStack from '../../../../components/SkeletonStack';
import Tabs, { TabOption } from '../../../../components/Tabs';
import { getCurrentUserId } from '../../../../services/auth';
import {
  useGetExamAttemptsByUserQuery,
} from '../../../../services/exams';
import { useGetStaffQuery } from '../../../../services/staff';

interface ExamLeaderboardRowProps {
  staffId: string;
  rank: number;
  points: number;
}

const ExamLeaderboardRow = ({ staffId, rank, points } : ExamLeaderboardRowProps) => {
  const {
    data: staff, isLoading: isStaffLoading,
  } = useGetStaffQuery({ staffId });

  const userId = getCurrentUserId();

  if (isStaffLoading) {
    return <Skeleton />;
  }

  if (!staff?.data) {
    return null;
  }

  return (
    <LeaderboardRow
      firstName={staff.data.firstName || ''}
      lastName={staff.data.lastName || ''}
      profileIcon={staff.data.profileIcon}
      rank={rank}
      points={points}
      isCurrentUser={staff.data.staffId === userId}
    />
  );
};

enum Tab {
  ThisMonth = 'this_month',
  ThisWeek = 'this_week',
}

const TABS: TabOption[] = [
  {
    key: Tab.ThisWeek,
    label: 'This Week',
  },
  {
    key: Tab.ThisMonth,
    label: 'This Month',
  },
];

const Leaderboard = () => {
  const {
    data: examAttempts, isLoading: isExamAttemptsLoading,
  } = useGetExamAttemptsByUserQuery({ marked: true });

  const [tab, setTab] = useState(Tab.ThisWeek);
  const userId = getCurrentUserId();

  const leaderboardData = useMemo(() => {
    if (examAttempts?.data) {
      // tallies the number of exams marked by each teacher this week
      const leaderboard = examAttempts.data.reduce((acc, attempt) => {
        const { markerId } = attempt;
        const isThisWeek = dayjs(attempt.markedDatetime).isSame(dayjs(), 'week');
        const isThisMonth = dayjs(attempt.markedDatetime).isSame(dayjs(), 'month');

        if ((tab === Tab.ThisWeek && isThisWeek) ||
          (tab === Tab.ThisMonth && isThisMonth)) {
          // Can't see a way to exclude quizzes apart from this - I can call exam types
          // endpoint but still can't tell if it's a quiz or not
          if (attempt.examLabel.includes('Exam')) acc[markerId] = (acc[markerId] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      // sorts the leaderboard by number of exams marked
      const sortedLeaderboard = Object.entries(leaderboard)
        .sort(([, a], [, b]) => b - a);
      return sortedLeaderboard;
    }
    return [];
  },
  [examAttempts, tab]);

  const displayedLeaderboard = useMemo(() => {
    const top10 = leaderboardData.slice(0, 10);
    const isCurrentUserInTop10 = top10.some(([markerId]) => markerId === userId);

    let currentUserData = null;
    let currentUserRank = null;

    if (!isCurrentUserInTop10) {
      const currentUserIndex = leaderboardData.findIndex(
        ([markerId]) => markerId === userId,
      );
      if (currentUserIndex !== -1) {
        currentUserData = leaderboardData[currentUserIndex];
        currentUserRank = currentUserIndex + 1; // Ranks are 1-based
      }
    }

    return {
      top10, currentUserData, currentUserRank,
    };
  }, [userId, leaderboardData]);

  return (
    <Stack direction="column" spacing={2}>
      <Tabs
        options={TABS}
        selectedKey={tab}
        onChange={(key) => {
          setTab(key as Tab);
        }}
        dense
      />
      {isExamAttemptsLoading ? (
        <SkeletonStack />
      ) : (
        <Stack spacing={1} direction="column">
          {displayedLeaderboard.top10.map(([markerId, count], index) => (
            <ExamLeaderboardRow
              key={markerId}
              staffId={markerId}
              rank={index + 1} // Index in top10 array + 1 for rank
              points={count}
            />
          ))}
          {(displayedLeaderboard.currentUserData &&
          displayedLeaderboard.currentUserRank) && (
            <ExamLeaderboardRow
              key={displayedLeaderboard.currentUserData[0]}
              staffId={displayedLeaderboard.currentUserData[0]}
              rank={displayedLeaderboard.currentUserRank} // Actual rank in full leaderboard
              points={displayedLeaderboard.currentUserData[1]}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default Leaderboard;
